import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { AG_GRID_DEFAULT_COL_DEF, AG_GRID_MODULES, AG_GRID_OPTIONS, ANNUAL_SUMMARY_TAB_ID, TOAST_CONTAINER_ID } from "../../../../shared/constants";
import { toast } from "react-toastify";
import { usaNumberFormatter, getToastOptions } from "../../../../shared/functions";
import { AxiosResponse } from "axios";
import useFetch from "../../../../hooks/useFetchMSAL";
import { useDispatch, useSelector } from "react-redux";
import 'ag-grid-enterprise';
import AnnualSummarySlicers from "./AnnualSummarySlicers";
import { forecastComparisonTabActions } from "../../../../store/slices/annual-summary-tab-slice";
import { RootState } from "../../../../store";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { AnnualSummarySelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/AnnualSummarySlicer.model";

const gridColNames: string[] = ["Brand", "Sub Brand", "Volume Size", "IBP Forecast", "Adjusted Baseline Forecast", "Promotional Lift", "Final Consumption Forecast", "Final Shipment Forecast", "Final Shipment Forecast vs IBP Demand Plan", "Trade Spend ($)", "Gross Sales ($)", "T:S Ratio (%)", "Profit ($)", "Profit Percent (%)", "Cost of Goods Sold ($)"];

const AnnualSummaryGrid = (props: { gridHeight: any }) => {
    const [, fetchData] = useFetch([]);
    const [gridRowData, setGridRowData] = useState<any[]>([]);
    const [gridRowDataCopy, setGridRowDataCopy] = useState<any[]>([]);
    const [gridColDef, setGridColDef] = useState<any[]>([]);
    const agGridRef = useRef<AgGridReact>(null);

    // Redux variables
    const dispatchFn = useDispatch();
    const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
    const selectedSlicers: AnnualSummarySelectedSlicerModel = useSelector((state: RootState) => state.annualSummaryTab.selectedSlicers);
    const tabApiDataFetched: boolean = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.tabApiDataFetched)[ANNUAL_SUMMARY_TAB_ID];
    const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
    const hierarchy: string = useSelector((state: RootState) => state.annualSummaryTab.hierarchy);

    /**
         * Returns ColDef for Volume Forecast data grid
         * @param {ReplenishmentPlanSummary[]} apiData Data from GET /annual-summary API
         * @param {PromotionDropdown[]} promotionDropdownData Promotion row dropdown data from GET /annual-summary API
         * @returns {any[]} Generated ColDef for Volume Forecast data grid
         */
    const generateColDef = useCallback((apiData: any): any[] => {
        const newColDef: any[] = [];

        if (apiData && (apiData.length > 0)) {
            newColDef.push(...gridColNames.map((col: string, index: number) => {
                const textRegExp: RegExp = /^[a-zA-Z_]+$/g;
                const isTextKey: boolean = textRegExp.test(col);
                return {
                    ...((index >= 3) ? {
                        colId: col,
                        minWidth: 100
                    } : {
                        width: 120
                    }),
                    field: col,
                    cellDataType: (isTextKey) ? "text" : "number",
                    ...(
                        (col === "Brand" || col === "Sub Brand") ? {
                            wrapText: true,
                            autoHeight: true
                        } : {}
                    ),
                    ...(
                        isTextKey || col === 'Sub Brand' ? {
                            cellClass: "ag-left-aligned-cell"
                        } : {
                            cellClass: "ag-right-aligned-cell"
                        }
                    ),
                    valueFormatter: (params: any): any => {
                        return (
                            (["Final Shipment Forecast vs IBP Demand Plan", "T:S Ratio (%)", "Profit Percent (%)"].includes(col)) ?
                                `${usaNumberFormatter(params.value * 100, 2)}%` : (["Trade Spend ($)", "Gross Sales ($)", "Profit ($)", "Cost of Goods Sold ($)"].includes(col)) ? `$${usaNumberFormatter(params.value, 0)}` : usaNumberFormatter(params.value, 0)
                        );

                    },
                    headerName: col,
                    headerTooltip: col,
                };
            }));
        }
        return newColDef;
    }, []);

    /**
    * Update grid data and slicers from fetch / update API response.
    * @param apiResponse API Response.
    */
    const updateGridDataAndSlicers = useCallback((apiResponse: any): void => {
        if (apiResponse.slicers) {
            dispatchFn(forecastComparisonTabActions.setSlicers(apiResponse.slicers))
        }
        console.log("api data: ", apiResponse);
        setGridRowData(structuredClone(apiResponse.result));
        setGridRowDataCopy(structuredClone(apiResponse.result));

        setGridColDef((prevColDef) => generateColDef(apiResponse.result));

        // dispatchFn(volumeForecastTabActions.setSlicers(structuredClone(apiResponse.slicers)));
    }, [dispatchFn, generateColDef]);

    const fetchAnnualSummaryData = useCallback(async () => {
        const fetchingDataToastID = toast.loading('Fetching Annual Summary data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<any> = await fetchData("/annual-summary", {
                method: "POST",
                data: {
                    slicers: {
                        customer_id: globalCustomer.id,
                        ...selectedSlicers
                    },
                    hierarchy: hierarchy
                }
            });

            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

            if (fetchAPIResponse.data.data) {
                updateGridDataAndSlicers(fetchAPIResponse.data.data);

                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [ANNUAL_SUMMARY_TAB_ID]: true
                }));

            } else {
                toast.error('Error in fetching Annual Summary data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Annual Summary data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    }, [selectedSlicers, globalCustomer.id, hierarchy, updateGridDataAndSlicers, dispatchFn, fetchData]);

    useEffect(() => {
        if (
            !tabApiDataFetched &&
            activeSubTabId === ANNUAL_SUMMARY_TAB_ID
        ) {
            fetchAnnualSummaryData();
        }
    }, [selectedSlicers, tabApiDataFetched, activeSubTabId, fetchAnnualSummaryData]);

    return (
        <>
            <div className="row">
                <div className="col col-12">
                    <AnnualSummarySlicers />
                </div>
            </div>

            <div className="ag-theme-balham mt-2" style={{ height: props.gridHeight, maxHeight: props.gridHeight, width: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                <AgGridReact
                    ref={agGridRef}
                    rowData={gridRowDataCopy}
                    columnDefs={gridColDef}
                    groupDisplayType={"multipleColumns"}
                    gridOptions={{
                        ...(AG_GRID_OPTIONS as any),
                        rowHeight: 22,
                        suppressMenuHide: true,
                        suppressColumnVirtualisation: true,
                        autoSizeStrategy: {
                            type: "fitCellContents",
                            skipHeader: true,
                            colIds: gridColNames.slice(3)
                        }
                        // isExternalFilterPresent: (params: any) => true,
                        // getRowStyle: gridRowStyleHandler,
                        // onCellValueChanged: cellValueChangeHandler,
                        // doesExternalFilterPass: (node: any): boolean => {
                        //     if (node.data) {
                        //         return node.data?.variable !== "promotion_calendar_id" && node.data?.variable !== "promotion_type";
                        //     }

                        //     return true;
                        // }
                    }}
                    defaultColDef={{
                        ...AG_GRID_DEFAULT_COL_DEF
                    }}
                    modules={[
                        ...AG_GRID_MODULES
                    ]}
                />
            </div>
        </>
    )
}
export default AnnualSummaryGrid